import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Target, Radio, TrendingUp, Calendar, Zap, Package, Store, Rocket, Sparkles, Bell, ChevronRight, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { guidanceAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import MomentumStreak from './MomentumStreak';

const ICONS = { target: Target, radio: Radio, trending: TrendingUp, calendar: Calendar, zap: Zap, package: Package, store: Store, rocket: Rocket, sparkles: Sparkles, bell: Bell };
const COLORS = {
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300',
  rose: 'border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-300',
  amber: 'border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-300',
  blue: 'border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300',
  violet: 'border-violet-200 bg-violet-50 text-violet-700 hover:border-violet-300',
  red: 'border-red-200 bg-red-50 text-red-700 hover:border-red-300',
};
const ICON_BG = {
  emerald: 'bg-emerald-100', rose: 'bg-rose-100', amber: 'bg-amber-100',
  blue: 'bg-blue-100', violet: 'bg-violet-100', red: 'bg-red-100',
};

export default function SmartDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    guidanceAPI.getDashboard()
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  return (
    <section className="py-6 px-4 bg-white border-b border-zinc-100" data-testid="smart-dashboard">
      <div className="max-w-5xl mx-auto">
        {/* Greeting */}
        <div className="mb-5">
          <h2 className="font-display font-bold text-xl text-zinc-900" data-testid="dashboard-greeting">
            Welcome back, {data.user_name || 'there'}
          </h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            {data.wallet_balance > 0 && <span>Wallet: <span className="font-semibold text-zinc-700">{formatCurrency(data.wallet_balance)}</span> · </span>}
            Here's what's happening
          </p>
        </div>

        {/* Continue where you left off */}
        {data.continue_items.length > 0 && (
          <div className="mb-5">
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Pick up where you left off</p>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {data.continue_items.map((item, idx) => (
                <Link
                  key={`${item.id}-${idx}`}
                  to={item.link}
                  className="flex-shrink-0 flex items-center gap-3 px-4 py-3 rounded-xl bg-zinc-50 border border-zinc-200 hover:border-zinc-300 hover:shadow-sm transition-all max-w-xs"
                  data-testid={`continue-item-${idx}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-zinc-200 flex items-center justify-center flex-shrink-0">
                    {item.type === 'project' ? <Target size={14} className="text-zinc-600" /> : <Package size={14} className="text-zinc-600" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-900 truncate">{item.title}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{item.subtitle}</p>
                  </div>
                  <ChevronRight size={14} className="text-zinc-400 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Smart cards */}
        {data.cards.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.cards.map((card, idx) => {
              const Icon = ICONS[card.icon] || Target;
              const colorClass = COLORS[card.color] || COLORS.emerald;
              const iconBg = ICON_BG[card.color] || ICON_BG.emerald;
              return (
                <motion.div
                  key={`${card.kind}-${idx}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.06 }}
                >
                  <Link
                    to={card.link}
                    className={`block p-4 rounded-xl border transition-all group ${colorClass}`}
                    data-testid={`dashboard-card-${card.kind}-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
                        <Icon size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold leading-tight">{card.title}</p>
                        <p className="text-[11px] opacity-70 mt-0.5 leading-snug">{card.subtitle}</p>
                      </div>
                      <ArrowRight size={14} className="opacity-40 group-hover:opacity-70 group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Momentum & Streaks */}
        <div className="mt-4">
          <MomentumStreak />
        </div>
      </div>
    </section>
  );
}
